import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Tabs, Tab, TextField, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Chip, Avatar, MenuItem, Select, FormControl, InputLabel,
  CircularProgress, Alert, Snackbar, Tooltip, Divider, InputAdornment,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { BASE_URL, apiCall } from '../dashboard/events/api/apiClient';

// ─── Theme ────────────────────────────────────────────────────────────────────
const gold        = '#16a34a';
const goldHover   = '#15803d';
const darkBg      = '#f0fdf4';
const cardBg      = '#ffffff';
const border      = 'rgba(34,197,94,.15)';
const textPrimary = '#0a0f0a';
const TitletextPrimary = '#ffffff';
const textSecondary = '#3d5245';
const textMuted   = '#64748b';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    color: '#0a0f0a',
    backgroundColor: '#f8fafc',
    '& fieldset': { borderColor: 'rgba(34,197,94,.2)' },
    '&:hover fieldset': { borderColor: 'rgba(34,197,94,.45)' },
    '&.Mui-focused fieldset': { borderColor: '#16a34a' },
  },
  '& .MuiInputLabel-root': { color: '#64748b' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#16a34a' },
  '& .MuiSelect-icon': { color: '#64748b' },
};
const getUserEntity = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.entity || user.entityId || '';
  } catch { return ''; }
};

// ─── Positions & Roles ────────────────────────────────────────────────────────
const positions = ['GK','CB','RB','LB','RWB','LWB','SW','CDM','CM','CAM','RM','LM','RW','LW','CF','ST'];
const roles = ['player' ,'scout', 'medical_staff', 'referee', 'fan', 'sponsor','president'];
const bloodTypes = ['A+','A-','B+','B-','AB+','AB-','O+','O-'];
const fitLevels = ['excellent','good','average','poor'];

// ─── Tab Panel ────────────────────────────────────────────────────────────────
const TabPanel = ({ children, value, index }) =>
  value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;

// ─── Chip color by status ─────────────────────────────────────────────────────
const statusColor = (s) => ({ active: 'success', inactive: 'default', suspended: 'error' }[s] || 'default');

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 1 — Entity Info
// ═══════════════════════════════════════════════════════════════════════════════
const EntityTab = () => {
  const entityId = getUserEntity();
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({});

  const fetch = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data: res } = await apiCall((headers) =>
        axios.get(`${BASE_URL}/entities/${entityId}`, { headers })
      );
      const entityData = res.data;
      setEntity(entityData);
      setForm({
        name: entityData.name || '',
        type: entityData.type || '',
        foundedYear: entityData.foundedYear || '',
        country: entityData.country || '',
        city: entityData.city || '',
        commune: entityData.commune || '',
        quartier: entityData.quartier || '',
        address: entityData.address || '',
        'contactInfo.email': entityData.contactInfo?.email || '',
        'contactInfo.phone': entityData.contactInfo?.phone || '',
        'contactInfo.website': entityData.contactInfo?.website || '',
        'socialMedia.instagram': entityData.socialMedia?.instagram || '',
        'socialMedia.twitter': entityData.socialMedia?.twitter || '',
        'socialMedia.facebook': entityData.socialMedia?.facebook || '',
      });
    } catch (e) {
      setError(e?.response?.data?.message || 'Erreur lors du chargement');
    } finally { setLoading(false); }
  }, [entityId]);

  useEffect(() => { fetch(); }, [fetch]);

  const change = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true); setError('');
    try {
      const payload = {
        name: form.name, type: form.type, foundedYear: form.foundedYear,
        country: form.country, city: form.city, commune: form.commune,
        quartier: form.quartier, address: form.address,
        contactInfo: { email: form['contactInfo.email'], phone: form['contactInfo.phone'], website: form['contactInfo.website'] },
        socialMedia: { instagram: form['socialMedia.instagram'], twitter: form['socialMedia.twitter'], facebook: form['socialMedia.facebook'] },
      };
      await apiCall((headers) =>
        axios.put(`${BASE_URL}/entity/${entityId}`, payload, { headers })
      );
      setSuccess('Entité mise à jour avec succès');
    } catch (e) {
      setError(e?.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: gold }} /></Box>;

  const F = ({ label, k, type = 'text', options }) => (
    <Grid item xs={12} sm={6}>
      {options ? (
        <FormControl fullWidth sx={inputSx}>
          <InputLabel>{label}</InputLabel>
          <Select value={form[k] || ''} label={label} onChange={change(k)}>
            {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </FormControl>
      ) : (
        <TextField fullWidth label={label} type={type} value={form[k] || ''} onChange={change(k)} sx={inputSx} />
      )}
    </Grid>
  );

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {entity && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          {entity.logo
            ? <Avatar src={entity.logo} sx={{ width: 64, height: 64, border: `2px solid ${gold}` }} />
            : <Avatar sx={{ width: 64, height: 64, bgcolor: 'rgba(212,175,55,.15)', border: `2px solid ${gold}` }}>
                <BusinessOutlinedIcon sx={{ color: gold }} />
              </Avatar>
          }
          <Box>
            <Typography variant="h5" sx={{ color: textPrimary, fontWeight: 800 }}>{entity.name}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: .5 }}>
              <Chip label={entity.type} size="small" sx={{ bgcolor: 'rgba(212,175,55,.15)', color: gold, fontWeight: 600 }} />
              {entity.isVerified && <Chip label="Vérifié" size="small" color="success" />}
            </Box>
          </Box>
        </Box>
      )}

      <Accordion defaultExpanded sx={{ bgcolor: cardBg, border: `1px solid ${border}`, borderRadius: '8px !important', mb: 2, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: gold }} />}>
          <Typography sx={{ color: textPrimary, fontWeight: 700 }}>Informations générales</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <F label="Nom" k="name" />
            <F label="Type" k="type" options={['club','academy','football_association']} />
            <F label="Année de fondation" k="foundedYear" type="number" />
            <F label="Pays" k="country" />
            <F label="Ville" k="city" />
            <F label="Commune" k="commune" />
            <F label="Quartier" k="quartier" />
            <F label="Adresse" k="address" />
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ bgcolor: cardBg, border: `1px solid ${border}`, borderRadius: '8px !important', mb: 2, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: gold }} />}>
          <Typography sx={{ color: textPrimary, fontWeight: 700 }}>Coordonnées</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <F label="E-mail" k="contactInfo.email" />
            <F label="Téléphone" k="contactInfo.phone" />
            <F label="Site web" k="contactInfo.website" />
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ bgcolor: cardBg, border: `1px solid ${border}`, borderRadius: '8px !important', mb: 3, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: gold }} />}>
          <Typography sx={{ color: textPrimary, fontWeight: 700 }}>Réseaux sociaux</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <F label="Instagram" k="socialMedia.instagram" />
            <F label="Twitter / X" k="socialMedia.twitter" />
            <F label="Facebook" k="socialMedia.facebook" />
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Button
        variant="contained" startIcon={saving ? <CircularProgress size={16} sx={{ color: darkBg }} /> : <SaveIcon />}
        onClick={save} disabled={saving}
        sx={{ bgcolor: gold, color: darkBg, fontWeight: 800, borderRadius: 2, px: 4, '&:hover': { bgcolor: goldHover } }}
      >
        {saving ? 'Enregistrement...' : 'Enregistrer'}
      </Button>

      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SF — Stable field component defined OUTSIDE CreateMemberDialog
// so React never unmounts/remounts it on each parent re-render.
// ═══════════════════════════════════════════════════════════════════════════════
const MemberFormField = ({ label, value, onChange, type = 'text', options }) => (
  <Grid item xs={12} sm={6}>
    {options ? (
      <FormControl fullWidth size="small" sx={inputSx}>
        <InputLabel>{label}</InputLabel>
        <Select value={value ?? ''} label={label} onChange={onChange}>
          {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </Select>
      </FormControl>
    ) : (
      <TextField
        fullWidth size="small" label={label} type={type}
        value={value ?? ''} onChange={onChange} sx={inputSx}
      />
    )}
  </Grid>
);

// ═══════════════════════════════════════════════════════════════════════════════
// CREATE MEMBER DIALOG
// ═══════════════════════════════════════════════════════════════════════════════
const defaultMemberForm = {
  firstName: '', lastName: '', phone: '', password: '123456',
  role: 'player', actualJerseyNumber: '', birthday: '', birthCity: 'Conakry',
  nationality: 'Guinean', positionOnField: 'ST', preferredFoot: 'right',
  medicalSituation: {
    bloodType: 'A+', height: '', weight: '',
    knownConditions: '', allergies: '', fitnessLevel: 'good',
  },
};

const CreateMemberDialog = ({ open, onClose, onCreated, entityId }) => {
  const [form, setForm] = useState(defaultMemberForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Stable handlers — useCallback so references don't change on every render
  const changeField = useCallback((k) => (e) =>
    setForm(f => ({ ...f, [k]: e.target.value })), []);

  const changeMedField = useCallback((k) => (e) =>
    setForm(f => ({ ...f, medicalSituation: { ...f.medicalSituation, [k]: e.target.value } })), []);

  // const submit = async () => {
  //   setLoading(true); setError('');
  //   try {
  //     const payload = {
  //       ...form,
  //       actualJerseyNumber: Number(form.actualJerseyNumber),
  //       entity: entityId,
  //       medicalSituation: {
  //         ...form.medicalSituation,
  //         height: Number(form.medicalSituation.height),
  //         weight: Number(form.medicalSituation.weight),
  //         knownConditions: form.medicalSituation.knownConditions
  //           ? form.medicalSituation.knownConditions.split(',').map(s => s.trim()) : [],
  //         allergies: form.medicalSituation.allergies
  //           ? form.medicalSituation.allergies.split(',').map(s => s.trim()) : [],
  //       },
  //     };
  //     await apiCall((headers) =>
  //       axios.post(`${BASE_URL}/auth/register`, payload, { headers })
  //     );
  //     onCreated();
  //     onClose();
  //     setForm(defaultMemberForm);
  //   } catch (e) {
  //     setError(e?.response?.data?.message || 'Erreur lors de la création');
  //   } finally { setLoading(false); }
  // };

  const submit = async () => {
  setLoading(true);
  setError('');
  try {
    const payload = {
      ...form,
      actualJerseyNumber: Number(form.actualJerseyNumber),
      entity: entityId,
      medicalSituation: {
        ...form.medicalSituation,
        height: Number(form.medicalSituation.height),
        weight: Number(form.medicalSituation.weight),
        knownConditions: form.medicalSituation.knownConditions
          ? form.medicalSituation.knownConditions.split(',').map(s => s.trim())
          : [],
        allergies: form.medicalSituation.allergies
          ? form.medicalSituation.allergies.split(',').map(s => s.trim())
          : [],
      },
    };

    // Step 1: Register the new user
    const registerResponse = await apiCall((headers) =>
      axios.post(`${BASE_URL}/auth/register`, payload, { headers })
    );

    const newUserId = registerResponse?.data?.data?.id
      || registerResponse?.data?.data?._id;

    if (!newUserId) throw new Error('User created but ID not returned from server');

    // Step 2: Add new user to the entity's member list
    await apiCall((headers) =>
      axios.post(
        `${BASE_URL}/entities/${entityId}/members`,
        {
          userId: newUserId,
          role: form.role,
          contractEndDate: '2049-06-30',   // adjust / add to form if needed
        },
        { headers }
      )
    );

    onCreated();
    onClose();
    setForm(defaultMemberForm);
  } catch (e) {
    setError(e?.response?.data?.message || e?.message || 'Erreur lors de la création');
  } finally {
    setLoading(false);
  }
};

  const sectionLabel = (text) => (
    <Typography sx={{ color: gold, fontWeight: 700, mb: 1.5, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1 }}>
      {text}
    </Typography>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
     PaperProps={{ sx: { bgcolor: '#ffffff', border: '1px solid rgba(34,197,94,.15)', borderRadius: 3 } }}
    // PaperProps={{ sx: { bgcolor: '#0f1528', border: `1px solid ${border}`, borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: textPrimary, fontWeight: 800, borderBottom: `1px solid ${border}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonOutlineIcon sx={{ color: gold }} /> Nouveau membre
        </Box>
        <IconButton onClick={onClose} sx={{ color: textSecondary }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {sectionLabel('Identité')}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <MemberFormField label="Prénom"           value={form.firstName}           onChange={changeField('firstName')} />
          <MemberFormField label="Nom"              value={form.lastName}            onChange={changeField('lastName')} />
          <MemberFormField label="Téléphone"        value={form.phone}               onChange={changeField('phone')} />
          <MemberFormField label="Mot de passe"     value={form.password}            onChange={changeField('password')} type="password" />
          <MemberFormField label="Rôle"             value={form.role}                onChange={changeField('role')} options={roles} />
          <MemberFormField label="N° Maillot"       value={form.actualJerseyNumber}  onChange={changeField('actualJerseyNumber')} type="number" />
          <MemberFormField label="Date de naissance" value={form.birthday}           onChange={changeField('birthday')} type="date" />
          <MemberFormField label="Ville de naissance" value={form.birthCity}         onChange={changeField('birthCity')} />
          <MemberFormField label="Nationalité"      value={form.nationality}         onChange={changeField('nationality')} />
        </Grid>

        {sectionLabel('Sur le terrain')}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <MemberFormField label="Position"     value={form.positionOnField} onChange={changeField('positionOnField')} options={positions} />
          <MemberFormField label="Pied préféré" value={form.preferredFoot}  onChange={changeField('preferredFoot')}  options={['right','left','both']} />
        </Grid>

        {sectionLabel('Informations médicales')}
        <Grid container spacing={2}>
          <MemberFormField label="Groupe sanguin"              value={form.medicalSituation.bloodType}       onChange={changeMedField('bloodType')}       options={bloodTypes} />
          <MemberFormField label="Taille (cm)"                 value={form.medicalSituation.height}          onChange={changeMedField('height')}          type="number" />
          <MemberFormField label="Poids (kg)"                  value={form.medicalSituation.weight}          onChange={changeMedField('weight')}          type="number" />
          <MemberFormField label="Niveau physique"             value={form.medicalSituation.fitnessLevel}    onChange={changeMedField('fitnessLevel')}    options={fitLevels} />
          <MemberFormField label="Conditions (virgule séparées)" value={form.medicalSituation.knownConditions} onChange={changeMedField('knownConditions')} />
          <MemberFormField label="Allergies (virgule séparées)"  value={form.medicalSituation.allergies}      onChange={changeMedField('allergies')} />
        </Grid>
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${border}`, px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: textSecondary }}>Annuler</Button>
        <Button variant="contained" onClick={submit} disabled={loading}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: darkBg }} /> : <AddIcon />}
          sx={{ bgcolor: gold, color: darkBg, fontWeight: 800, borderRadius: 2, '&:hover': { bgcolor: goldHover } }}>
          Créer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Reset Password Dialog ─────────────────────────────────────────────────────
const ResetPasswordDialog = ({ open, onClose, userId, memberName }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const submit = async () => {
    if (!password) { setError('Mot de passe requis'); return; }
    setLoading(true); setError('');
    try {
      await apiCall((headers) =>
        // axios.patch(`${BASE_URL}/users/${userId}/adminpassword`, { password }, { headers })
      axios.patch(`${BASE_URL}/users/${userId}/adminpassword`, { newPassword: password }, { headers })
      );
      setSuccess('Mot de passe réinitialisé');
      setTimeout(() => { onClose(); setPassword(''); setSuccess(''); }, 1500);
    } catch (e) {
      setError(e?.response?.data?.message || 'Erreur');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
     PaperProps={{ sx: { bgcolor: '#ffffff', border: '1px solid rgba(34,197,94,.15)', borderRadius: 3 } }}
    //  PaperProps={{ sx: { bgcolor: '#0f1528', border: `1px solid ${border}`, borderRadius: 3 } }}
    >
      <DialogTitle sx={{ color: textPrimary, fontWeight: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${border}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockResetIcon sx={{ color: gold }} /> Réinitialiser MDP
        </Box>
        <IconButton onClick={onClose} sx={{ color: textSecondary }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Typography sx={{ color: textSecondary, mb: 2 }}>{memberName}</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <TextField fullWidth label="Nouveau mot de passe" type="password" value={password}
          onChange={e => setPassword(e.target.value)} sx={inputSx} />
      </DialogContent>
      <DialogActions sx={{ borderTop: `1px solid ${border}`, px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: textSecondary }}>Annuler</Button>
        <Button variant="contained" onClick={submit} disabled={loading}
          sx={{ bgcolor: gold, color: darkBg, fontWeight: 800, borderRadius: 2, '&:hover': { bgcolor: goldHover } }}>
          Réinitialiser
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 2 — Members
// ═══════════════════════════════════════════════════════════════════════════════
const MembersTab = () => {
  const entityId = getUserEntity();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [resetDialog, setResetDialog] = useState({ open: false, userId: '', name: '' });
  const [editRow, setEditRow] = useState(null);
  const [savingRow, setSavingRow] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [snack, setSnack] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data: res } = await apiCall((headers) =>
        axios.get(`${BASE_URL}/entities/${entityId}`, { headers })
      );
      setMembers(res.data?.teamMembers || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Erreur lors du chargement');
    } finally { setLoading(false); }
  }, [entityId]);

  useEffect(() => { load(); }, [load]);

  const saveEdit = async (userId) => {
    setSavingRow(editRow.index);
    try {
      await apiCall((headers) =>
        axios.put(`${BASE_URL}/entities/${entityId}/members/${userId}`, editRow.data, { headers })
      );
      setSnack('Membre mis à jour');
      setEditRow(null);
      load();
    } catch (e) {
      setSnack(e?.response?.data?.message || 'Erreur');
    } finally { setSavingRow(null); }
  };

  const removeMember = async (userId) => {
    setDeletingId(userId);
    try {
      await apiCall((headers) =>
        axios.delete(`${BASE_URL}/entities/${entityId}/members/${userId}`, { headers })
      );
      setSnack('Membre retiré');
      load();
    } catch (e) {
      setSnack(e?.response?.data?.message || 'Erreur');
    } finally { setDeletingId(null); }
  };

  const fullName = (m) => {
    const u = m.userId;
    if (typeof u === 'object' && u !== null) return `${u.firstName || ''} ${u.lastName || ''}`.trim();
    return u || '—';
  };

  const userId = (m) => (typeof m.userId === 'object' ? m.userId?._id : m.userId);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
          sx={{ bgcolor: gold, color: darkBg, fontWeight: 800, borderRadius: 2, '&:hover': { bgcolor: goldHover } }}>
          Nouveau membre
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: gold }} /></Box>
      ) : members.length === 0 ? (
        <Paper sx={{ p: 6, borderRadius: 3, textAlign: 'center', bgcolor: cardBg, border: `1px solid ${border}` }}>
          <GroupOutlinedIcon sx={{ fontSize: 60, color: gold, mb: 2, opacity: .4 }} />
          <Typography variant="h6" sx={{ color: textSecondary, fontWeight: 600 }}>Aucun membre</Typography>
          <Typography variant="body2" sx={{ color: textMuted, mt: 1 }}>Ajoutez des membres à votre entité.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ bgcolor: cardBg, border: `1px solid ${border}`, borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': { color: gold, fontWeight: 700, borderBottom: `1px solid ${border}`, bgcolor: 'rgba(212,175,55,.05)' } }}>
                <TableCell>Membre</TableCell>
                <TableCell>Rôle</TableCell>
                <TableCell>Position</TableCell>
                <TableCell>Maillot</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((m, i) => {
                const isEditing = editRow?.index === i;
                const uid = userId(m);
                return (
                  <TableRow key={uid || i} sx={{ '& td': { borderColor: border, color: textPrimary }, '&:hover': { bgcolor: 'rgba(255,255,255,.02)' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'rgba(212,175,55,.15)', color: gold, fontSize: 14, fontWeight: 700 }}>
                          {fullName(m).charAt(0) || '?'}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{fullName(m)}</Typography>
                          <Typography sx={{ fontSize: 12, color: textMuted }}>{uid?.slice(-6) || '—'}</Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      {isEditing ? (
                        <FormControl size="small" sx={{ minWidth: 140, ...inputSx }}>
                          <Select value={editRow.data.role} onChange={e => setEditRow(r => ({ ...r, data: { ...r.data, role: e.target.value } }))}>
                            {roles.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                          </Select>
                        </FormControl>
                      ) : <Typography sx={{ fontSize: 13 }}>{m.role}</Typography>}
                    </TableCell>

                    <TableCell>
                      {isEditing ? (
                        <FormControl size="small" sx={{ minWidth: 100, ...inputSx }}>
                          <Select value={editRow.data.positionOnField || ''} onChange={e => setEditRow(r => ({ ...r, data: { ...r.data, positionOnField: e.target.value } }))}>
                            <MenuItem value="">—</MenuItem>
                            {positions.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                          </Select>
                        </FormControl>
                      ) : <Chip label={m.positionOnField || '—'} size="small" sx={{ bgcolor: 'rgba(255,255,255,.07)', color: textSecondary, fontWeight: 600 }} />}
                    </TableCell>

                    <TableCell>
                      {isEditing ? (
                        <TextField size="small" type="number" value={editRow.data.jerseyNumber || ''} sx={{ width: 80, ...inputSx }}
                          onChange={e => setEditRow(r => ({ ...r, data: { ...r.data, jerseyNumber: e.target.value } }))} />
                      ) : <Typography sx={{ fontSize: 13 }}>#{m.jerseyNumber || '—'}</Typography>}
                    </TableCell>

                    <TableCell>
                      {isEditing ? (
                        <FormControl size="small" sx={{ minWidth: 110, ...inputSx }}>
                          <Select value={editRow.data.status} onChange={e => setEditRow(r => ({ ...r, data: { ...r.data, status: e.target.value } }))}>
                            {['active','inactive','suspended'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                          </Select>
                        </FormControl>
                      ) : <Chip label={m.status} color={statusColor(m.status)} size="small" />}
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: .5, justifyContent: 'flex-end' }}>
                        {isEditing ? (
                          <>
                            <Tooltip title="Enregistrer">
                              <IconButton 
                              onClick={() => saveEdit(uid)}
                              
                              disabled={savingRow === i} size="small" sx={{ color: gold }}>
                                {savingRow === i ? <CircularProgress size={16} sx={{ color: gold }} /> : <SaveIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Annuler">
                              <IconButton onClick={() => setEditRow(null)} size="small" sx={{ color: textMuted }}><CloseIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <Tooltip title="Modifier">
                            <IconButton onClick={() => setEditRow({ index: i, data: { role: m.role, status: m.status, jerseyNumber: m.jerseyNumber, positionOnField: m.positionOnField, userId: uid } })}
                              size="small" sx={{ color: textSecondary, '&:hover': { color: gold } }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Réinitialiser MDP">
                          <IconButton onClick={() => setResetDialog({ open: true, userId: uid, name: fullName(m) })}
                            size="small" sx={{ color: textSecondary, '&:hover': { color: '#60a5fa' } }}>
                            <LockResetIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Retirer">
                          <IconButton onClick={() => removeMember(uid)} disabled={deletingId === uid}
                            size="small" sx={{ color: textSecondary, '&:hover': { color: '#f87171' } }}>
                            {deletingId === uid ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CreateMemberDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} entityId={entityId} />
      <ResetPasswordDialog open={resetDialog.open} onClose={() => setResetDialog({ open: false, userId: '', name: '' })}
        userId={resetDialog.userId} memberName={resetDialog.name} />

      <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="info" onClose={() => setSnack('')}>{snack}</Alert>
      </Snackbar>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const ManageEntity = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: TitletextPrimary, fontWeight: 900 }}>Gérer l'Entité</Typography>
        <Typography variant="body2" sx={{ color: textMuted, mt: .5 }}>Administration de votre club ou académie</Typography>
      </Box>

      <Box sx={{ borderBottom: `1px solid ${border}`, mb: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          TabIndicatorProps={{ style: { backgroundColor: gold } }}
          sx={{
            '& .MuiTab-root': { color: textSecondary, fontWeight: 600, textTransform: 'none', fontSize: 15, minHeight: 48 },
            '& .Mui-selected': { color: gold },
          }}>
          <Tab icon={<BusinessOutlinedIcon />} iconPosition="start" label="Gestion de l'entité" />
          <Tab icon={<GroupOutlinedIcon />} iconPosition="start" label="Gestion des membres" />
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}><EntityTab /></TabPanel>
      <TabPanel value={tab} index={1}><MembersTab /></TabPanel>
    </Box>
  );
};

export default ManageEntity;